import PageHero from "../components/site/PageHero";
import CTABanner from "../components/site/CTABanner";
import FAQBlock from "../components/site/FAQBlock";
import useSEO from "../hooks/useSEO";

export default function FAQs() {
  useSEO({
    title: "FAQs | Fire Safety Systems Ireland",
    description:
      "Answers to common questions about fire alarm systems, emergency lighting, fire extinguishers, testing, servicing and planned maintenance.",
    canonicalPath: "/faqs",
  });

  const GENERAL = [
    {
      q: "What fire safety services does FSS provide?",
      a: "We provide fire alarm systems, emergency lighting, fire extinguishers, scheduled testing and certification, planned preventative maintenance, and repairs and upgrades for existing systems.",
    },
    {
      q: "Does FSS work with existing fire safety systems?",
      a: "Yes. We support both new installations and existing systems that require maintenance, repair, extension or upgrading.",
    },
    {
      q: "Can FSS install a completely new fire alarm system?",
      a: "Yes — from site survey and system design through to installation, commissioning, testing and handover.",
    },
    {
      q: "What areas does FSS cover?",
      a: "We provide fire safety services to businesses and organisations throughout Ireland. If you are unsure whether we service your area, please contact us.",
    },
    {
      q: "How do I arrange a site survey?",
      a: "Use the enquiry form on this website or call us on 01 835 1454. We will discuss your premises, current systems and requirements and arrange a suitable visit.",
    },
    {
      q: "What information is needed to prepare a quotation?",
      a: "Typically we will need to understand the type and size of your premises, any installed systems, the service you require, and the site location. For larger works we may recommend a site survey before quoting.",
    },
    {
      q: "Does FSS provide service reports and documentation?",
      a: "Yes. Following completed work we provide clear service information and appropriate documentation covering the equipment tested, work carried out and any recommended remedial actions.",
    },
    {
      q: "Can FSS support customers across multiple sites?",
      a: "Yes. We can provide coordinated servicing schedules, central reporting and consistent processes for organisations operating across multiple premises.",
    },
  ];

  const FIRE_ALARMS = [
    {
      q: "How often should a fire alarm system be tested?",
      a: "Testing intervals depend on the type of system, the size of the premises and the applicable standards. As part of a planned maintenance programme we will agree an appropriate schedule with you. Please contact us for advice specific to your building.",
    },
    {
      q: "Can FSS repair faults in an existing alarm system?",
      a: "In most cases yes — subject to the manufacturer, age and condition of the installed equipment. We assess the system, identify the fault where practicable, and advise on repair or upgrade options.",
    },
    {
      q: "Can FSS extend or upgrade an existing alarm system?",
      a: "Often yes — depending on the capacity of the existing panel and the availability of compatible devices from the original manufacturer.",
    },
    {
      q: "What information affects fire alarm quotations?",
      a: "Typical factors include building size and layout, number of floors, occupancy and use, number and type of devices, existing wiring and infrastructure, and any integration or accessibility requirements.",
    },
  ];

  const EMERGENCY_LIGHTING = [
    {
      q: "How often should emergency lighting be inspected?",
      a: "The inspection schedule depends on the installed system and the requirements applicable to your building. We can agree a suitable schedule as part of a maintenance plan.",
    },
    {
      q: "Can emergency lighting batteries be replaced?",
      a: "Yes. Battery replacement is a routine part of emergency lighting servicing.",
    },
    {
      q: "What happens if a fitting fails a test?",
      a: "Failed fittings are typically reported to you with a recommendation to repair or replace, depending on the type and condition of the equipment.",
    },
  ];

  const EXTINGUISHERS = [
    {
      q: "How often should fire extinguishers be serviced?",
      a: "Extinguishers are usually serviced on an annual cycle, with additional periodic checks recommended between visits. We will confirm an appropriate schedule for your premises.",
    },
    {
      q: "What extinguisher types does FSS supply?",
      a: "We can supply and service Water, CO₂, Dry Powder and Wet Chemical extinguishers. The correct type for your premises depends on the risks and activities on site — we recommend a site survey before finalising the provision.",
    },
    {
      q: "Can FSS service extinguishers supplied by another company?",
      a: "In most cases yes. We can inspect the existing extinguishers and confirm what is required as part of the visit.",
    },
    {
      q: "What happens to obsolete or discharged extinguishers?",
      a: "We arrange responsible disposal of obsolete or unfit extinguishers as part of the service where required.",
    },
  ];

  const MAINTENANCE = [
    {
      q: "Can FSS provide planned maintenance across multiple sites?",
      a: "Yes — we can coordinate a scheduled programme, provide site-by-site reporting and maintain central records for the portfolio.",
    },
    {
      q: "What is included in a planned maintenance programme?",
      a: "Planned maintenance typically covers scheduled servicing, functional testing, fault diagnosis, minor repairs, component replacement where required, and detailed reporting.",
    },
    {
      q: "What records are provided?",
      a: "We provide clear service information following each visit — including the equipment tested, faults identified and any recommended remedial actions.",
    },
    {
      q: "Are business operations disrupted during servicing?",
      a: "We aim to work around your operating hours and minimise disruption. Where testing requires temporary system silencing, we agree suitable arrangements in advance.",
    },
  ];

  const PROCESS = [
    {
      q: "What happens during a site survey?",
      a: "During a survey we walk the premises, review the installed systems, discuss any current issues and confirm the scope of work required. Following the survey we provide a clear proposal and quotation.",
    },
    {
      q: "How long does an installation or service usually take?",
      a: "Duration varies significantly depending on the size and complexity of the work. We provide indicative timescales as part of the quotation.",
    },
    {
      q: "What information does FSS need before visiting?",
      a: "Typically your address, a brief description of the premises, the service you require and any known issues. Photos of the installed equipment or panel — where possible — can also help.",
    },
    {
      q: "What happens after the work is completed?",
      a: "We provide relevant documentation, discuss any recommended follow-up work, and can agree ongoing servicing if required.",
    },
  ];

  return (
    <>
      <PageHero
        eyebrow="FAQs"
        title="Frequently asked questions."
        subtitle="Answers to common questions about our services, the process and how we support customers with new and existing fire safety systems."
        ctaPrimary={{ to: "/contact", label: "Speak to Our Team" }}
        ctaSecondary={{ to: "/services", label: "Explore Services" }}
      />

      <FAQBlock eyebrow="General" title="About FSS and our services" items={GENERAL} />
      <FAQBlock eyebrow="Fire Alarm Systems" title="Fire alarm questions" items={FIRE_ALARMS} bg="slate" />
      <FAQBlock eyebrow="Emergency Lighting" title="Emergency lighting questions" items={EMERGENCY_LIGHTING} />
      <FAQBlock eyebrow="Fire Extinguishers" title="Fire extinguisher questions" items={EXTINGUISHERS} bg="slate" />
      <FAQBlock eyebrow="Maintenance" title="Servicing and maintenance questions" items={MAINTENANCE} />
      <FAQBlock eyebrow="Process" title="How we work" items={PROCESS} bg="slate" />

      <CTABanner
        title="Question we haven't answered?"
        subtitle="Send us a note and a member of our team will get back to you."
        primary={{ to: "/contact", label: "Contact Our Team" }}
        secondary={{ to: "/services", label: "Explore Services" }}
      />
    </>
  );
}
